import { Button } from "@Light/components/page/Button";
import { useScaffold } from "@Light/scaffold";
import clsx from "clsx";
import React, { createContext, useContext, useState } from "react";
import { BottomSheet } from "./BottomSheet";
import { SupportContactRows } from "@Light/components/enroll/SupportContactRows.tsx";

export type SupportBottomSheetProps = {
  open: boolean;
  onClose: () => void;
};

/**
 * Support bottom sheet, presenting the "Have questions?" help contents in a
 * {@link BottomSheet}. Controlled via `open`/`onClose`.
 */
export const SupportBottomSheet: React.FC<SupportBottomSheetProps> = ({
  open,
  onClose,
}) => {
  return (
    <BottomSheet open={open} onClose={onClose}>
      <SupportBottomSheetContents onClose={onClose} />
    </BottomSheet>
  );
};

const SupportBottomSheetContext = createContext<(() => void) | null>(null);

export type SupportBottomSheetProviderProps = {
  children: React.ReactNode;
};

/**
 * Provides a single {@link SupportBottomSheet} that is reusable via
 * {@link useSupportBottomSheet} hook.
 */
export const SupportBottomSheetProvider: React.FC<
  SupportBottomSheetProviderProps
> = ({ children }) => {
  const [open, setOpen] = useState(false);
  const openSupport = () => {
    setOpen(true);
  };

  return (
    <SupportBottomSheetContext.Provider value={openSupport}>
      {children}
      <SupportBottomSheet
        open={open}
        onClose={() => {
          setOpen(false);
        }}
      />
    </SupportBottomSheetContext.Provider>
  );
};

export function useSupportBottomSheet(): () => void {
  const openSupport = useContext(SupportBottomSheetContext);
  if (!openSupport) {
    throw new Error(
      "useSupportBottomSheet must be used within SupportBottomSheetProvider",
    );
  }
  return openSupport;
}

type SupportBottomSheetContentsProps = {
  onClose: () => void;
};

const SupportBottomSheetContents: React.FC<SupportBottomSheetContentsProps> = ({
  onClose,
}) => {
  const scaffold = useScaffold();

  return (
    <div className="flex flex-col gap-6 px-4 pt-12 pb-6">
      <h2
        className={clsx(
          "text-2xl font-semibold leading-8",
          scaffold.page.colors.text.primary,
        )}
      >
        Have questions?
        <br />
        We&rsquo;re happy to answer them.
      </h2>

      <SupportContactRows />

      <Button
        size="lg"
        buttonType="outline"
        className="w-full"
        onClick={() => {
          onClose();
        }}
      >
        Close
      </Button>
    </div>
  );
};
